import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const policyItems = [
  {
    id: 'A',
    title: 'Not Professional Advice; Not an Expert Report',
    content: (
      <ul className="list-disc pl-4 space-y-1">
        <li>is <strong>NOT</strong> legal advice</li>
        <li>is <strong>NOT</strong> an engineering opinion</li>
        <li>is <strong>NOT</strong> a licensed on-site fire investigation opinion</li>
        <li>is <strong>NOT</strong> an insurance coverage determination</li>
        <li>is <strong>NOT</strong> an affidavit or retained expert report</li>
        <li>does not create an attorney-client relationship, expert-client relationship, insurer-insured relationship, or fiduciary duty</li>
      </ul>
    ),
  },
  {
    id: 'B',
    title: 'Not for Emergency Response / Life Safety Decisions',
    content: (
      <p>Do not use a Report or the Service for emergency response, imminent danger situations, or real-time life safety decisions. If there is an emergency, contact local emergency services immediately.</p>
    ),
  },
  {
    id: 'C',
    title: 'Inputs and Scope Limitations',
    content: (
      <div className="space-y-2">
        <p>Each Report is limited to what is visible in submitted materials and what the user provided in text. The system did not:</p>
        <ul className="list-disc pl-4 space-y-1">
          <li>conduct a site visit</li>
          <li>collect physical samples</li>
          <li>perform laboratory testing</li>
          <li>perform electrical measurements</li>
          <li>interview witnesses</li>
          <li>conduct destructive examination</li>
        </ul>
        <p>If something is not visible or not provided, the Report may state "Not observable / Not provided."</p>
      </div>
    ),
  },
  {
    id: 'D',
    title: 'AI Uncertainty; No Guarantees',
    content: (
      <p>AI systems can produce inaccurate, incomplete, or misleading outputs. Any confidence scoring or probability ranges are heuristic estimates, not guarantees. Real outcomes depend on factors outside the system's control (jurisdiction, admissibility, witness credibility, evidence preservation, site conditions, and additional evidence).</p>
    ),
  },
  {
    id: 'E',
    title: 'Standards References',
    content: (
      <p>A Report may reference standards or frameworks (e.g., NFPA 921, NFPA 1033, ASTM E2713, NEC, NEMA guidance, IEEE/UL guidance) to structure analysis. Such references do not mean the Report is certified, compliant, complete, or admissible.</p>
    ),
  },
  {
    id: 'F',
    title: 'Decision Warning / No Reliance & Duty to Verify',
    content: (
      <p>Do not make legal, insurance, financial, or safety decisions solely based on a Report. Consult qualified licensed professionals for definitive determinations. Customers are solely responsible for independently verifying all facts, findings, and references generated in the Report before using it in any professional, legal, or claims-handling capacity. You assume all risk for actions taken in reliance on a Report.</p>
    ),
  },
  {
    id: 'G',
    title: 'No Chain-of-Custody / Evidence Preservation',
    content: (
      <p>FireCaseIQ does not provide evidence preservation, chain-of-custody controls, or forensic integrity guarantees for uploaded materials. Preserve original evidence and maintain any chain-of-custody required for claims or litigation.</p>
    ),
  },
  {
    id: 'H',
    title: 'Optional Expert Review',
    content: (
      <p>FireCaseIQ may allow engagement of independent Experts for review. Experts are independent contractors and are solely responsible for their professional advice and deliverables.</p>
    ),
  },
  {
    id: 'I',
    title: 'Mandatory Disclaimers and Notices',
    content: (
      <p>Every Report generated by the Service includes a built-in notice stating that it is an AI-generated preliminary report subject to this Policy. You agree not to remove, alter, or obscure these notices before sharing the Report with any third party.</p>
    ),
  },
];

const ReportDisclaimer = () => {
  const [expanded, setExpanded] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  return (
    <div className="border border-warning/40 rounded-lg overflow-hidden">
      {/* Header — always visible */}
      <div className="bg-warning/10 px-4 py-5 flex items-start gap-3">
        <Icon name="AlertTriangle" size={22} className="text-warning flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-3">
          <p className="font-bold text-foreground text-sm">
            AI-Generated Preliminary Report Notice
          </p>

          <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">Important Notice – AI-Generated Analysis</p>
            <p>
              This report was generated using artificial intelligence through the FireCaseIQ platform.
              It is provided solely as a preliminary analytical tool based on materials submitted by
              the user (such as photographs, documents, notes, and metadata).
            </p>
            <p>
              This report is not a professional fire investigation opinion and should not be treated as
              a definitive determination of fire origin, cause, liability, or coverage.
            </p>
            <p>This report:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>is not legal advice</li>
              <li>is not an engineering opinion</li>
              <li>is not a licensed fire investigation report</li>
              <li>is not an expert witness report</li>
              <li>does not establish chain-of-custody for evidence</li>
              <li>may contain errors, omissions, or incorrect interpretations</li>
            </ul>
            <p>
              The system does not perform site inspections, laboratory testing, electrical
              measurements, or physical evidence collection. All analysis is limited to the
              information provided to the platform.
            </p>
            <p>
              Artificial intelligence systems can generate outputs that are incomplete, inaccurate,
              or misleading, and any conclusions, probability estimates, or confidence scores should
              be treated as preliminary observations only.
            </p>

            <p className="font-semibold text-foreground pt-1">Not for Legal or Safety Decisions</p>
            <p>
              This report should not be used as the sole basis for legal, insurance, investigative,
              financial, or safety decisions. Users should consult qualified licensed professionals
              and independently verify all findings before relying on this report in any professional
              or legal context.
            </p>

            <p className="font-semibold text-foreground pt-1">Evidence Handling</p>
            <p>
              FireCaseIQ does not provide evidence preservation or forensic chain-of-custody
              guarantees. Original evidence and investigative records should be preserved
              independently in accordance with applicable investigative procedures.
            </p>

            <p className="font-semibold text-foreground pt-1">Full Policy and Limitations</p>
            <p>
              For a complete explanation of the limitations, responsibilities, and legal terms
              governing AI-generated reports, please review the full policy:{' '}
              <a
                href="https://www.firecaseiq.com/report-disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                firecaseiq.com/report-disclaimer
              </a>
            </p>

            <p className="font-semibold text-foreground pt-1">Strong Recommendation</p>
            <p>
              We strongly recommend that all users read and understand the full policy before using
              or sharing this report, particularly if it may be referenced in investigative,
              insurance, or legal contexts.
            </p>

            <p className="italic border-t border-warning/20 pt-2 mt-2">
              By accessing or using this report, you acknowledge that it is an AI-generated
              preliminary analysis subject to the limitations described in the policy.
            </p>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-warning font-medium hover:underline mt-1"
          >
            {expanded ? 'Hide detailed policy' : 'View detailed policy sections'}
            <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
          </button>
        </div>
      </div>

      {/* Expandable full policy */}
      {expanded && (
        <div className="bg-background border-t border-warning/20 px-4 py-4 space-y-2">
          <div className="text-xs text-muted-foreground leading-relaxed space-y-1 pb-2 border-b border-border">
            <p className="font-medium text-foreground text-xs">AI-Generated Preliminary Report Policy (FireCaseIQ)</p>
            <p>Effective Date: February 15, 2026 &nbsp;|&nbsp; Last Updated: February 27, 2026</p>
            <p>
              This Policy explains the disclosures and limitations that apply to any AI-generated preliminary
              report produced through FireCaseIQ. By accessing, downloading, or using any Report, you
              acknowledge and agree to this Policy. Reports are generated using AI systems (including large
              language models and APIs provided by third parties such as OpenAI) based solely on materials
              submitted by the user.
            </p>
          </div>

          {policyItems.map((item) => (
            <div key={item.id} className="border border-border rounded-md overflow-hidden">
              <button
                onClick={() => setOpenSection(openSection === item.id ? null : item.id)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-card hover:bg-muted/50 transition-colors text-left"
              >
                <span className="text-xs font-semibold text-foreground flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-warning/20 text-warning flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    {item.id}
                  </span>
                  {item.title}
                </span>
                <Icon
                  name={openSection === item.id ? 'ChevronUp' : 'ChevronDown'}
                  size={14}
                  className="text-muted-foreground flex-shrink-0 ml-2"
                />
              </button>
              {openSection === item.id && (
                <div className="px-3 py-3 border-t border-border text-xs text-muted-foreground leading-relaxed bg-background">
                  {item.content}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportDisclaimer;
